import React, { useState, useEffect } from "react";
import { NavLink, Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { LogOut, Menu, ArrowLeftCircle, LayoutDashboard } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { NAV, ROLE_LABEL, ROLE_HOME } from "@/lib/nav";
import NotificationDrawer from "@/components/NotificationDrawer";
import CelebrationOverlay from "@/components/CelebrationOverlay";
import TermsGate from "@/components/TermsGate";
import ChoicePrompt from "@/components/ChoicePrompt";
import { FeatureFlagContext } from "@/lib/featureFlags";
import api from "@/lib/api";

const maskLastName = (n) => { n = (n || "").trim(); return n ? `${n[0]}.` : ""; };
const maskEmail = (e) => {
  e = (e || "").trim();
  if (!e || !e.includes("@")) return e;
  const [local, domain] = e.split("@");
  return `${local.slice(0, 2) + "x".repeat(Math.max(local.length - 2, 3))}@${domain}`;
};

function SidebarContent({ items, onNavigate, badges = {} }) {
  return (
    <nav className="flex flex-col gap-1 px-3 py-4 custom-scroll overflow-y-auto">
      {items.map((item) => {
        const count = badges[item.to] || 0;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            data-testid={`nav-${item.to.split("/").pop()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-slate-100 text-slate-900 border-l-4 border-sky-500 pl-2"
                  : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"
              }`
            }
          >
            <item.icon className="h-[18px] w-[18px] shrink-0" />
            <span className="truncate">{item.label}</span>
            {count > 0 && (
              <span className="ml-auto inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[11px] font-bold text-white" data-testid={`nav-badge-${item.to.split("/").pop()}`}>
                {count}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

function Brand({ act }) {
  const line = act ? "RallyPointGo - Act" : "RallyPointGo";
  return (
    <div className="flex items-center gap-2.5 px-5 py-4 border-b border-slate-200">
      <img src="/rallypointgo-icon.png" alt={line} className="h-[6.75rem] w-[6.75rem] object-contain" />
      <div className="leading-tight">
        <p className="text-lg font-extrabold tracking-tight text-slate-900 font-heading" data-testid="brand-wordmark">
          RallyPoint<span className="text-orange-500">Go</span>
          {act && <span className="block text-base" data-testid="brand-act"><span className="font-semibold text-slate-400">- </span><span className="text-rose-600">Act</span></span>}
        </p>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-sky-600">Management System</p>
        {line && <p className="mt-4 text-[11px] font-medium text-slate-500 leading-tight" data-testid="brand-assignment">{line}</p>}
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout, impersonating, stopImpersonating, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const viewOnly = !!user.view_only;
  useEffect(() => {
    if (!viewOnly) return;
    const root = document.getElementById("app-main");
    if (!root) return;
    const ACTION_RE = /\b(send|add|new|edit|delete|remove|create|approve|reject|deny|save|submit|issue|request|invite|assign|import|upload|expunge|revoke|deactivate|reactivate|reset|change|update|schedule|duplicate|generate|mark|record)\b/i;
    const TID_RE = /(send|add|new|edit|delete|remove|create|approve|reject|deny|submit|invite|assign|import|upload|expunge|revoke|deactivate|reset|save|issue|request)/i;
    const apply = () => {
      root.querySelectorAll("button").forEach((b) => {
        if (b.dataset.roGuarded) return;
        const tid = b.getAttribute("data-testid") || "";
        if (tid === "stop-impersonation-btn") return;
        const txt = (b.textContent || "").trim();
        if (ACTION_RE.test(txt) || (tid && TID_RE.test(tid))) {
          b.style.opacity = "0.4";
          b.style.pointerEvents = "none";
          b.setAttribute("aria-disabled", "true");
          b.title = "Read-only view — changes are disabled";
          b.dataset.roGuarded = "1";
        }
      });
    };
    apply();
    const obs = new MutationObserver(apply);
    obs.observe(root, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [viewOnly, location.pathname]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [badges, setBadges] = useState({});
  const [flags, setFlags] = useState({ merit_tracking: true, document_depot: false, ledger: false, choices: true, phonebook: true, schedule: true, rally_books: true, parent_account: true, district_admin_account: true, campus_admin_account: true, attendance: false, conflicts: false, volunteers: false });
  const [animEnabled, setAnimEnabled] = useState(() => localStorage.getItem("rallypointgo_animations") === "on");
  const [flagsReady, setFlagsReady] = useState(false);
  const gateCtx = { ...user, ff_document_depot: flags.document_depot, ff_ledger: flags.ledger,
    ff_merit: flags.merit_tracking !== false, ff_lettering: flags.lettering !== false, ff_rally_books: flags.rally_books !== false,
    ff_choices: flags.choices !== false, ff_phonebook: flags.phonebook !== false, ff_schedule: flags.schedule !== false,
    ff_attendance: flags.attendance === true, ff_conflicts: flags.conflicts === true, ff_thespian: flags.thespian === true, ff_bios: flags.bios === true, ff_show_notes: flags.show_notes === true };
  const items = (NAV[user.role] || []).filter((i) => !i.gate || gateCtx[i.gate]).filter((i) => !(viewOnly && ["/coach/phonebook", "/coach/profile"].includes(i.to)));
  const onDashboard = location.pathname === ROLE_HOME[user.role];

  // Re-sync the user on navigation so gated nav items (e.g. Announcements for officers)
  // reflect changes made mid-session (like a coach enabling "Can draft").
  useEffect(() => { refresh(); }, [location.pathname, refresh]);

  // Feature flags gate Document Depot / Ledger for non-admins; refreshed on navigation or "rpg:flags-refresh".
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/feature-flags")
      .then(({ data }) => {
        if (cancelled) return;
        setFlags(data);
        setFlagsReady(true);
        const on = !!data.animations;
        setAnimEnabled(on);
        localStorage.setItem("rallypointgo_animations", on ? "on" : "off");
      })
      .catch(() => {});
    load();
    window.addEventListener("rpg:flags-refresh", load);
    return () => { cancelled = true; window.removeEventListener("rpg:flags-refresh", load); };
  }, [location.pathname]);

  // Theatre-plan teams are branded "RallyPointGo - Act" (browser tab too).
  useEffect(() => {
    document.title = flags.theatre_brand ? "RallyPointGo - Act | Management System" : "RallyPointGo | Management System";
    return () => { document.title = "RallyPointGo | Management System"; };
  }, [flags.theatre_brand]);

  // Coach: Team Members nav badge = pending announcement drafts + pending signup applications.
  useEffect(() => {
    if (user.role !== "coach") return;
    let cancelled = false;
    const loadBadges = () => {
      Promise.all([
        api.get("/coach/announcement-drafts?status=pending").then(({ data }) => data.pending_count || 0).catch(() => 0),
        api.get("/coach/assignment-requests").then(({ data }) => (data.items || []).filter((i) => i.assignment_status === "pending").length).catch(() => 0),
      ]).then(([drafts, apps]) => { if (!cancelled) setBadges((b) => ({ ...b, "/coach/members": drafts + apps })); });
      Promise.all(["/conflicts/pending-count", "/volunteers/swaps/pending-count"].map((u) => api.get(u).then(({ data }) => data.count || 0).catch(() => 0)))
        .then(([c, s]) => { if (!cancelled) setBadges((b) => ({ ...b, "/coach/schedule": c + s })); });
    };
    loadBadges();
    window.addEventListener("rpg:badges-refresh", loadBadges);
    return () => { cancelled = true; window.removeEventListener("rpg:badges-refresh", loadBadges); };
  }, [user.role, location.pathname]);

  // Coach: pending Show Notes count on the Show Notes nav item.
  useEffect(() => {
    if (user.role !== "coach" || flags.show_notes !== true) return;
    const load = () => api.get("/show-notes/pending-count")
      .then(({ data }) => setBadges((b) => ({ ...b, "/coach/show-notes": data.count || 0 }))).catch(() => {});
    load();
    window.addEventListener("rpg:badges-refresh", load);
    return () => window.removeEventListener("rpg:badges-refresh", load);
  }, [user.role, flags.show_notes, location.pathname]);

  // System Admin: unread notification count on the Dashboard nav item (unread live on the dashboard card).
  useEffect(() => {
    if (user.role !== "administrator") return undefined;
    const load = () => api.get("/notifications/unread-count")
      .then(({ data }) => setBadges((b) => ({ ...b, "/admin/dashboard": data.count || 0 }))).catch(() => {});
    load();
    const id = setInterval(load, 20000);
    window.addEventListener("rpg:badges-refresh", load);
    return () => { clearInterval(id); window.removeEventListener("rpg:badges-refresh", load); };
  }, [user.role, location.pathname]);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const doStopImpersonating = () => {
    stopImpersonating();
  };

  const initials = `${(user.first_name || "")[0] || ""}${(user.last_name || "")[0] || ""}`.toUpperCase();

  // Data masking: team members & parents see their own name/email masked in the header too.
  const isMember = user.role === "team_member";
  const isParent = user.role === "parent";
  const hdrFirst = isParent ? "xxxxx" : user.first_name;
  const hdrLast = isMember ? maskLastName(user.last_name) : user.last_name;
  const hdrEmail = (isMember || isParent) ? maskEmail(user.email) : user.email;
  const hdrInitials = isParent ? `${(user.last_name || "")[0] || ""}`.toUpperCase() : initials;

  return (
    <div className={`flex min-h-screen bg-slate-50${animEnabled ? "" : " animations-off"}`}>
      <TermsGate />
      <CelebrationOverlay enabled={animEnabled} ready={flagsReady} />
      <ChoicePrompt enabled={gateCtx.ff_choices} ready={flagsReady} />
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-slate-200 bg-white fixed inset-y-0 z-30">
        <Brand act={flags.theatre_brand === true} />
        <div className="flex-1 overflow-y-auto">
          <SidebarContent items={items} badges={badges} />
        </div>
        <button
          onClick={doLogout}
          data-testid="logout-btn"
          className="m-3 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors"
        >
          <LogOut className="h-[18px] w-[18px]" /> Sign out
        </button>
      </aside>

      <div className="flex-1 min-w-0 lg:pl-64">
        {impersonating && (
          <div className={`flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm font-semibold text-white sm:px-6 ${viewOnly ? "bg-sky-600" : "bg-amber-500"}`} data-testid="impersonation-banner">
            <span>{viewOnly ? "Read-only view" : "Viewing as"} {user.first_name} {user.last_name} ({ROLE_LABEL[user.role]}){viewOnly ? " — you cannot make changes." : " — actions are audited."}</span>
            <button onClick={doStopImpersonating} data-testid="stop-impersonation-btn" className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 hover:bg-white/30 transition-colors">
              <ArrowLeftCircle className="h-4 w-4" /> Return to my account
            </button>
          </div>
        )}
        {/* Header */}
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-4 border-b border-slate-200 bg-white/80 px-4 backdrop-blur-md sm:px-6">
          <div className="flex items-center gap-2">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden flex h-10 w-10 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100" data-testid="mobile-menu-trigger">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 flex flex-col">
                <div className="shrink-0">
                  <Brand act={flags.theatre_brand === true} />
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto custom-scroll" data-testid="mobile-nav-scroll">
                  <SidebarContent items={items} onNavigate={() => setMobileOpen(false)} badges={badges} />
                </div>
                <button
                  onClick={doLogout}
                  data-testid="logout-btn-mobile-menu"
                  className="m-3 flex shrink-0 items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                >
                  <LogOut className="h-[18px] w-[18px]" /> Sign out
                </button>
              </SheetContent>
            </Sheet>
            <span className="hidden sm:inline text-sm font-semibold text-slate-400">{ROLE_LABEL[user.role]}</span>
            {!onDashboard && (
              <Link to={ROLE_HOME[user.role]} data-testid="header-dashboard-link"
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors">
                <LayoutDashboard className="h-4 w-4" /> Dashboard
              </Link>
            )}
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <NotificationDrawer />
            <div className="flex items-center gap-2.5 pl-1">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-100 text-sm font-bold text-sky-700" data-testid="user-avatar">
                {hdrInitials || "U"}
              </div>
              <div className="hidden sm:block leading-tight">
                <p className="text-sm font-semibold text-slate-800" data-testid="header-user-name">{hdrFirst} {hdrLast}</p>
                <p className="text-xs text-slate-400" data-testid="header-user-email">{hdrEmail}</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="lg:hidden text-slate-500" onClick={doLogout} data-testid="logout-btn-mobile">
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </header>

        <main id="app-main" className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <FeatureFlagContext.Provider value={{ plan: flags.plan, theatre_brand: flags.theatre_brand === true, merit_tracking: flags.merit_tracking !== false, lettering: flags.lettering !== false, choices: flags.choices !== false, phonebook: flags.phonebook !== false, schedule: flags.schedule !== false, rally_books: flags.rally_books !== false, document_depot: flags.document_depot === true, document_upload: flags.document_upload === true, animations: !!flags.animations, parent_account: flags.parent_account !== false, district_admin_account: flags.district_admin_account !== false, campus_admin_account: flags.campus_admin_account !== false, attendance: flags.attendance === true, conflicts: flags.conflicts === true, volunteers: flags.volunteers === true, thespian: flags.thespian, bios: flags.bios, show_notes: flags.show_notes }}>
            <Outlet />
          </FeatureFlagContext.Provider>
        </main>
      </div>
    </div>
  );
}
